import { accountingApi } from '../config/apiClient';
import { ACCOUNTING_API } from '../config/api';

// DTO Interfaces
export interface InvoiceItemDto {
  itemName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  sctRate?: number;
  discountRate?: number;
  lineTotal: number;
  vatAmount: number;
  sctAmount?: number;
  discountAmount?: number;
  netTotal: number;
  grossTotal: number;
  warehouseId?: string;
  itemId?: string;
  isManualItem?: boolean;
  priceType?: string;
}

export interface InvoiceDto {
  id: string;
  invoiceNumber: string;
  invoiceType: 'SALES' | 'PURCHASE' | 'QUICK_SALE';
  accountId: string;
  accountName: string;
  invoiceDate: string;
  dueDate?: string;
  subtotal: number;
  vatTotal: number;
  sctTotal: number;
  discountTotal: number;
  grandTotal: number;
  tevkifatRate?: number;
  tevkifatAmount?: number;
  tevkifatType?: string;
  stopajRate?: number;
  stopajAmount?: number;
  currency: string;
  exchangeRate: number;
  notes?: string;
  status: 'draft' | 'completed' | 'canceled';
  branchId?: string;
  paymentType?: string;
  transactionType?: string;
  description?: string;
  isClosed: boolean;
  items: InvoiceItemDto[];
  createdAt: string;
  updatedAt?: string;
  createdBy: string;
  updatedBy?: string;
}

export interface CreateInvoiceDto {
  /**
   * Şirket ID. Backend `[Required(ErrorMessage = "Şirket ID zorunludur")]` ile
   * `[ApiController]` model-state filter'ı action öncesi devreye girdiğinden
   * **MUTLAKA payload'da gönderilmelidir** (controller'daki `request.CompanyId = companyId`
   * fallback'i çalışmaz; auto-validation 400 üretir).
   */
  companyId: string;
  invoiceNumber: string;
  invoiceType: 'SALES' | 'PURCHASE' | 'QUICK_SALE';
  accountId: string;
  accountName: string;
  invoiceDate: string;
  dueDate?: string;
  subtotal: number;
  vatTotal: number;
  sctTotal: number;
  discountTotal: number;
  grandTotal: number;
  tevkifatRate?: number;
  tevkifatAmount?: number;
  tevkifatType?: string;
  stopajRate?: number;
  stopajAmount?: number;
  currency: string;
  exchangeRate: number;
  notes?: string;
  status: 'draft' | 'completed' | 'canceled';
  branchId?: string;
  paymentType?: string;
  transactionType?: string;
  description?: string;
  isClosed: boolean;
  items: InvoiceItemDto[];
  createdBy: string;
  /**
   * ✅ PR3 (Invoice Seller Snapshot Refactor): Backend SellerResolver için ekran bilgisi.
   * QuickSale/Restaurant/Package/Pyk/CallCenter ekranlarından kayıt edilen faturalarda
   * gönderilir. Manuel fatura akışları (SalesInvoice/PurchaseInvoice) için "Manual" gönderilir
   * veya boş bırakılır — bu durumda resolver companyId'den Companies tablosuna düşer.
   */
  screenId?: number;
  screenType?: 'QuickSale' | 'Restaurant' | 'Package' | 'Pyk' | 'CallCenter' | 'Manual';
}

export interface UpdateInvoiceDto {
  invoiceNumber?: string;
  accountId?: string;
  accountName?: string;
  invoiceDate?: string;
  dueDate?: string;
  subtotal?: number;
  vatTotal?: number;
  sctTotal?: number;
  discountTotal?: number;
  grandTotal?: number;
  tevkifatRate?: number;
  tevkifatAmount?: number;
  tevkifatType?: string;
  stopajRate?: number;
  stopajAmount?: number;
  currency?: string;
  exchangeRate?: number;
  notes?: string;
  status?: 'draft' | 'completed' | 'canceled';
  branchId?: string;
  paymentType?: string;
  transactionType?: string;
  description?: string;
  isClosed?: boolean;
  items?: InvoiceItemDto[];
  updatedBy: string;
}

export interface DeleteInvoiceDto {
  deletedBy: string;
}

export interface NextInvoiceNumberDto {
  invoiceNumber: string;
  invoiceType: string;
  count: number;
}

// Invoice Service
export const invoiceService = {
  /**
   * Şirkete ait tüm faturaları getir
   */
  async getInvoicesByCompany(): Promise<InvoiceDto[]> {
    console.log('📋 [invoiceService] Şirket faturaları çekiliyor');
    const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_COMPANY());
    console.log(`✅ [invoiceService] ${response.data.data?.length || 0} fatura çekildi`);
    return response.data.data || [];
  },

  async getInvoicesByCompanyFiltered(invoiceType?: string): Promise<InvoiceDto[]> {
    const maxPageSize = 100;
    let allData: InvoiceDto[] = [];
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
      const url = ACCOUNTING_API.INVOICE_BY_COMPANY({
        invoiceType: invoiceType || undefined,
        pageSize: maxPageSize,
        page,
      });
      const response = await accountingApi.get(url);
      const data = response.data.data || [];
      const totalPages = response.data.totalPages || 1;
      allData = allData.concat(data);
      
      if (data.length < maxPageSize || page >= totalPages) {
        hasMore = false;
      }
      page++;
      if (page > 50) break;
    }
    
    return allData;
  },

  /**
   * Şirkete ait satış faturalarını getir
   */
  // ⚠️ GEÇİCİ: Aşağıdaki fonksiyonlar backend'de henüz implement edilmediği için yoruma alındı
  // TODO: Backend'de ilgili endpoint'ler hazırlandığında aktif edilecek
  
  // async getSalesInvoicesByCompany(companyId: string): Promise<InvoiceDto[]> {
  //   console.log('📋 [invoiceService] Satış faturaları çekiliyor:', companyId);
  //   const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_COMPANY(companyId) + '?type=SALES');
  //   console.log(`✅ [invoiceService] ${response.data.data?.length || 0} satış faturası çekildi`);
  //   return response.data.data || [];
  // },

  // async getPurchaseInvoicesByCompany(companyId: string): Promise<InvoiceDto[]> {
  //   console.log('📋 [invoiceService] Alış faturaları çekiliyor:', companyId);
  //   const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_COMPANY(companyId) + '?type=PURCHASE');
  //   console.log(`✅ [invoiceService] ${response.data.data?.length || 0} alış faturası çekildi`);
  //   return response.data.data || [];
  // },

  async getInvoicesByDateRange(startDate: string, endDate: string, invoiceType?: string, filterField?: string): Promise<InvoiceDto[]> {
    const baseUrl = ACCOUNTING_API.INVOICE_BY_COMPANY().replace(/\/company(?:\/[^/]*)?/, '/reports');
    const params = new URLSearchParams({
      startDate,
      endDate,
    });
    if (invoiceType) params.set('invoiceType', invoiceType);
    if (filterField) params.set('filterField', filterField);
    const response = await accountingApi.get(`${baseUrl}?${params.toString()}`);
    return response.data.data || [];
  },

  async getInvoiceById(id: string): Promise<InvoiceDto> {
    console.log('📋 [invoiceService] Fatura detayı çekiliyor:', { id });
    const response = await accountingApi.get(ACCOUNTING_API.INVOICE_BY_ID(id));
    console.log('✅ [invoiceService] Fatura detayı çekildi');
    return response.data.data;
  },

  // async getInvoicesByAccount(companyId: string, accountId: string): Promise<InvoiceDto[]> {
  //   console.log('📋 [invoiceService] Cari faturaları çekiliyor:', { companyId, accountId });
  //   // TODO: Backend endpoint eklenecek
  //   return [];
  // },

  // async searchInvoices(companyId: string, query: string): Promise<InvoiceDto[]> {
  //   console.log('🔍 [invoiceService] Fatura aranıyor:', { companyId, query });
  //   // TODO: Backend endpoint eklenecek
  //   return [];
  // },

  // async getNextInvoiceNumber(companyId: string, type: 'SALES' | 'PURCHASE'): Promise<NextInvoiceNumberDto> {
  //   console.log('📋 [invoiceService] Sonraki fatura numarası alınıyor:', { companyId, type });
  //   // TODO: Backend'de otomatik numara üretimi var, bu fonksiyon şimdilik gereksiz
  //   return { invoiceNumber: 'AUTO' };
  // },

  /**
   * Yeni fatura oluştur (✅ SQL API)
   */
  async createInvoice(data: CreateInvoiceDto): Promise<InvoiceDto> {
    console.log('💾 [invoiceService] Yeni fatura oluşturuluyor:', data.invoiceNumber);
    const response = await accountingApi.post(ACCOUNTING_API.INVOICE_CREATE, data);
    console.log('✅ [invoiceService] Fatura oluşturuldu:', response.data.data.id);
    return response.data.data;
  },

  /**
   * Fatura güncelle
   */
  async updateInvoice(id: string, data: UpdateInvoiceDto): Promise<InvoiceDto> {
    console.log('💾 [invoiceService] Fatura güncelleniyor:', id);
    const response = await accountingApi.post(ACCOUNTING_API.INVOICE_UPDATE(id), data);
    console.log('✅ [invoiceService] Fatura güncellendi');
    return response.data.data;
  },

  /**
   * Fatura sil (soft delete)
   */
  async deleteInvoice(id: string, deletedBy: string): Promise<boolean> {
    console.log('🗑️ [invoiceService] Fatura siliniyor:', id);
    const response = await accountingApi.post(ACCOUNTING_API.INVOICE_DELETE(id), { deletedBy });
    console.log('✅ [invoiceService] Fatura silindi');
    return response.data.success;
  },
};

